import {
  AtUri,
  type AppBskyFeedDefs,
  type BskyAgent,
} from '@atproto/api'
import {TID} from '@atproto/common-web'

import {uploadBlob} from '#/lib/api'
import {getPostVideo} from '#/lib/feed/mediaHelpers'
import {
  extractSegmentDurationMsFromPost,
  extractTotalDurationMsFromVideoPage,
  isTVSegmentPost,
} from './tvParser'
import {type TVMasterRecord, type TVSegment} from './tvTypes'

const segmentSrcCache = new Map<
  string,
  Promise<{src: string; poster?: string}>
>()
const thumbnailUrlCache = new Map<string, string>()

export async function createMasterRecord(
  agent: BskyAgent,
  record: Omit<TVMasterRecord, '$type'>,
) {
  const rkey = TID.next().toString()
  const res = await agent.com.atproto.repo.putRecord({
    repo: agent.assertDid,
    collection: 'app.tellus.video.master',
    rkey,
    record: {
      $type: 'app.tellus.video.master',
      ...record,
    },
  })
  return res.data
}

function getBlobCid(blob: {ref?: unknown; cid?: string; $link?: string}): string | undefined {
  const ref = blob?.ref as { $link?: string } | string | undefined
  return (
    (typeof ref === 'string' ? ref : ref?.$link) ||
    blob?.cid ||
    (blob as { $link?: string } | undefined)?.$link
  )
}

function getPdsBaseUrl(agent: BskyAgent): string | undefined {
  const anyAgent = agent as any
  const url =
    anyAgent?.pdsUrl?.toString?.() ||
    anyAgent?.sessionManager?.pdsUrl?.toString?.() ||
    anyAgent?.dispatchUrl?.toString?.()
  if (!url) return undefined
  try {
    return new URL(url).origin
  } catch {
    return undefined
  }
}

export async function uploadThumbnailBlob(
  agent: BskyAgent,
  input: Blob | string,
  mimeType?: string,
): Promise<{cid?: string; mime?: string; blob?: unknown}> {
  const res = await uploadBlob(agent, input, mimeType)
  const blob = res.data.blob as {ref?: unknown; cid?: string; $link?: string; mimeType?: string}
  return {
    cid: getBlobCid(blob),
    mime: blob?.mimeType || mimeType,
    blob,
  }
}

export function getThumbnailUrl(
  agent: BskyAgent,
  masterUri: string,
  master: TVMasterRecord,
): string | undefined {
  if (!master.thumbnailCid) return undefined
  const cacheKey = `${masterUri}:${master.thumbnailCid}`
  const cached = thumbnailUrlCache.get(cacheKey)
  if (cached) return cached
  const pdsBase = getPdsBaseUrl(agent)
  if (!pdsBase) return undefined
  const urip = new AtUri(masterUri)
  const url = `${pdsBase}/xrpc/com.atproto.sync.getBlob?did=${encodeURIComponent(
    urip.host,
  )}&cid=${encodeURIComponent(master.thumbnailCid)}`
  thumbnailUrlCache.set(cacheKey, url)
  return url
}

export async function getMasterRecord(
  agent: BskyAgent,
  uri: string,
): Promise<TVMasterRecord> {
  const urip = new AtUri(uri)
  const res = await agent.com.atproto.repo.getRecord({
    repo: urip.host,
    collection: urip.collection,
    rkey: urip.rkey,
  })
  return res.data.value as TVMasterRecord
}

export async function resolveSegmentUriToSrc(
  agent: BskyAgent,
  uri: string,
): Promise<{src: string; poster?: string}> {
  const cached = segmentSrcCache.get(uri)
  if (cached) return cached

  const promise = (async () => {
    const urip = new AtUri(uri)
    if (!urip.host.startsWith('did:')) {
      const res = await agent.resolveHandle({handle: urip.host})
      urip.host = res.data.did
    }
    const res = await agent.getPosts({uris: [urip.toString()]})
    const post = res.data.posts[0]
    if (!post) {
      throw new Error('Segment post not found')
    }
    const video = getPostVideo(post)
    if (!video?.url) {
      throw new Error('Segment video source missing')
    }
    return {src: video.url, poster: video.thumb}
  })()

  segmentSrcCache.set(uri, promise)
  return promise
}

function isThreadPost(
  value: unknown,
): value is AppBskyFeedDefs.ThreadViewPost {
  return (
    !!value &&
    typeof value === 'object' &&
    (value as AppBskyFeedDefs.ThreadViewPost).post !== undefined
  )
}

export async function resolveLongVideoThread(
  agent: BskyAgent,
  rootUri: string,
): Promise<{segments: TVSegment[]; totalDurationMs?: number}> {
  const res = await agent.app.bsky.feed.getPostThread({
    uri: rootUri,
    depth: 100,
  })

  if (!isThreadPost(res.data.thread)) {
    throw new Error('Thread root not found')
  }

  const segments: TVSegment[] = []
  let current: AppBskyFeedDefs.ThreadViewPost | undefined = res.data.thread

  while (current) {
    const durationMs = extractSegmentDurationMsFromPost(current.post)
    segments.push({
      uri: current.post.uri,
      cid: current.post.cid,
      durationMs: durationMs || 0,
      order: segments.length,
    })

    const replies = Array.isArray(current.replies)
      ? current.replies.filter(isThreadPost)
      : []
    if (!replies.length) break

    const next =
      replies.find(reply => isTVSegmentPost(reply.post)) ||
      replies.find(reply => reply.post.author.did === current!.post.author.did) ||
      replies[0]
    if (!next) break
    current = next
  }

  const totalDurationMs = segments.reduce(
    (acc, segment) => acc + (segment.durationMs || 0),
    0,
  )
  const totalFromRoot = extractTotalDurationMsFromVideoPage(res.data.thread.post)

  return {
    segments,
    totalDurationMs: totalFromRoot || totalDurationMs || undefined,
  }
}
